// The counter poster (issue #13, US-6): what an A4 or A5 poster says, in which languages, and the rules the PDF
// renderer in apps/api/src/lib/poster.ts follows to print it (bidirectional runs, Arabic marks). No I/O here.
//
// A poster belongs to one pickup point: its QR code is the pickup point's customer URL (`/p/<id>`, no token; the page
// issues a short-lived scan token itself). It is printed in the venue's default language plus one secondary language,
// chosen by the owner among the customer-page languages (the page a diner lands on must speak what the poster spoke).
// Copy follows chartegraphique/assets/multilingual-copy.md section 4, adapted to the real flow: scan, send the
// prefilled WhatsApp message, collect when told (no phone number is typed on the page).
import { SUPPORTED_LANGS, isLang, type Lang, type NumberMode } from './ticket';

/**
 * Bump when the layout, the copy or the embedded fonts change: stored posters are keyed by a fingerprint that
 * includes it, so every poster is rebuilt on its next download.
 */
export const POSTER_VERSION = 1;

export type PosterSize = 'a4' | 'a5';
export const POSTER_SIZES_LIST: readonly PosterSize[] = ['a4', 'a5'];

/** Page sizes in PDF points (1/72 inch): ISO 216, portrait. A5 is A4 scaled by 1/sqrt(2), so one layout serves both. */
export const POSTER_SIZES: Record<PosterSize, { width: number; height: number; label: string }> = {
  a4: { width: 595.28, height: 841.89, label: 'A4' },
  a5: { width: 419.53, height: 595.28, label: 'A5' },
};

/** Generation budget of US-6, measured around rendering and storage. */
export const POSTER_BUDGET_MS = 5000;

export function parsePosterSize(input: unknown): PosterSize | null {
  const s = typeof input === 'string' ? input.trim().toLowerCase() : '';
  return s === 'a4' || s === 'a5' ? s : null;
}

// ---- copy ----

export interface PosterStep {
  /** One word, printed large: the three words of the instruction, linked by arrows. */
  title: string;
  /** The rest of the sentence, printed under the word. */
  body: string;
}

export interface PosterCopy {
  headline: string;
  /** The line about the till, by number mode: scan mode shows the number to the cashier, ticket mode types the receipt number. */
  cashier_scan: string;
  cashier_ticket: string;
  steps: readonly [PosterStep, PosterStep, PosterStep];
  no_whatsapp: string;
  /** Before the printed link, for a phone whose camera does not read QR codes. */
  link_hint: string;
}

/** The poster copy per language. Arabic is Modern Standard Arabic; Darija is not printed (not in release 1). */
export const POSTER_COPY: Record<Lang, PosterCopy> = {
  fr: {
    headline: 'On vous prévient quand c’est prêt.',
    cashier_scan: 'Après le paiement, scannez ce code et montrez votre numéro à la caisse.',
    cashier_ticket: 'Après le paiement, scannez ce code et saisissez le numéro de votre ticket.',
    steps: [
      { title: 'Scannez', body: 'le code avec l’appareil photo' },
      { title: 'Envoyez', body: 'le message WhatsApp préparé' },
      { title: 'Récupérez', body: 'votre commande quand on vous prévient' },
    ],
    no_whatsapp: 'Pas de WhatsApp ? Gardez la page ouverte : elle sonnera.',
    link_hint: 'Ou ouvrez ce lien',
  },
  en: {
    headline: 'We’ll let you know when it’s ready.',
    cashier_scan: 'After paying, scan this code and show your number at the till.',
    cashier_ticket: 'After paying, scan this code and enter your receipt number.',
    steps: [
      { title: 'Scan', body: 'the code with your camera' },
      { title: 'Send', body: 'the ready-made WhatsApp message' },
      { title: 'Collect', body: 'your order when we let you know' },
    ],
    no_whatsapp: 'No WhatsApp? Keep the page open: it will ring.',
    link_hint: 'Or open this link',
  },
  ar: {
    headline: 'نُخبرك عندما يصبح طلبك جاهزًا.',
    cashier_scan: 'بعد الدفع، امسح هذا الرمز واعرض رقمك على أمين الصندوق.',
    cashier_ticket: 'بعد الدفع، امسح هذا الرمز وأدخل رقم تذكرتك.',
    steps: [
      { title: 'امسح', body: 'الرمز بكاميرا هاتفك' },
      { title: 'أرسل', body: 'الرسالة الجاهزة على واتساب' },
      { title: 'استلم', body: 'طلبك عندما نخبرك' },
    ],
    no_whatsapp: 'لا تستخدم واتساب؟ أبقِ الصفحة مفتوحة، وسترنّ.',
    link_hint: 'أو افتح هذا الرابط',
  },
};

// ---- languages ----

/**
 * The poster's languages, default first. The secondary language is the owner's choice when it is still one of the
 * venue's languages and not the default; otherwise the first other venue language (French, Arabic, English order);
 * a venue with a single language gets a single-language poster.
 */
export function posterLanguages(v: { defaultLang: Lang; languages: readonly Lang[]; posterLang2: Lang | null | undefined }): Lang[] {
  const others = SUPPORTED_LANGS.filter((l) => l !== v.defaultLang && v.languages.includes(l));
  const chosen = v.posterLang2 && others.includes(v.posterLang2) ? v.posterLang2 : (others[0] ?? null);
  return chosen ? [v.defaultLang, chosen] : [v.defaultLang];
}

/** The secondary languages the settings page offers: the venue's languages other than the default. */
export function posterSecondaryOptions(defaultLang: Lang, languages: readonly Lang[]): Lang[] {
  return SUPPORTED_LANGS.filter((l) => l !== defaultLang && languages.includes(l));
}

/**
 * Validates `poster_lang_2` of a settings body against the languages and default the venue will have after the
 * change: null or "" clears the choice (automatic second language); a language must be one of the venue's languages
 * and not the default.
 */
export function parsePosterLang2(input: unknown, after: { languages: readonly Lang[]; defaultLang: Lang }): { value: Lang | null; error?: undefined } | { value?: undefined; error: 'bad_poster_lang' } {
  if (input === null || input === '') return { value: null };
  if (!isLang(input) || input === after.defaultLang || !after.languages.includes(input)) return { error: 'bad_poster_lang' };
  return { value: input };
}

// ---- text rules for the PDF ----

/**
 * Arabic harakat, Quranic marks and the superscript alef. The PDF engine places glyphs by their advance widths and
 * does not apply mark positioning, so marks would land beside their letter; signage is written without them anyway.
 */
const ARABIC_MARKS = /[\u0610-\u061A\u064B-\u065F\u0670\u06D6-\u06DC\u06DF-\u06E4\u06E7\u06E8\u06EA-\u06ED]/g;

export function stripArabicMarks(text: string): string {
  return text.replace(ARABIC_MARKS, '');
}

/** Bidi controls and other invisible format characters an owner may paste into a name; the renderer orders runs itself. */
const FORMAT_CHARS = /[\u200B-\u200F\u202A-\u202E\u2066-\u2069\uFEFF]/g;

/** A name or copy line as the poster prints it: marks and format characters removed, whitespace collapsed. */
export function posterText(text: string): string {
  return stripArabicMarks(text).replace(FORMAT_CHARS, '').replace(/\s+/g, ' ').trim();
}

const ARABIC_CHAR = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
/** True when the text has an Arabic character: the line needs the Arabic face and its taller line height. */
export function hasArabic(text: string): boolean {
  return ARABIC_CHAR.test(text);
}

/** Letters and digits of any script other than Arabic: they read left to right. */
const LTR_CHAR = /[\p{L}\p{N}]/u;

export type TextDir = 'ltr' | 'rtl';

/** A run of one direction, its characters in logical order. RTL runs print in the Arabic face, LTR runs in the Latin one. */
export interface TextRun {
  text: string;
  dir: TextDir;
}

/** Arabic-Indic digits: inside the Arabic block, but numbers read left to right like Western digits. */
const ARABIC_DIGIT = /[٠-٩۰-۹]/;

function charDir(ch: string): TextDir | null {
  if (ARABIC_DIGIT.test(ch)) return 'ltr';
  if (ARABIC_CHAR.test(ch)) return 'rtl';
  if (LTR_CHAR.test(ch)) return 'ltr';
  return null; // space, punctuation, symbol: takes the direction of its neighbours
}

/**
 * The direction of a paragraph: the direction of its first letter (Unicode rule P2; digits are weak and do not
 * count), else `fallback`.
 */
export function paragraphDir(text: string, fallback: TextDir = 'ltr'): TextDir {
  for (const ch of text) {
    if (/\p{N}/u.test(ch)) continue;
    const d = charDir(ch);
    if (d) return d;
  }
  return fallback;
}

/**
 * One line of text split into direction runs, in visual order from left to right. A simplified Unicode bidi
 * algorithm that covers what a poster prints (Arabic copy, Latin names, digits, a URL): letters and digits are strong,
 * a neutral between two runs of the same direction joins them, any other neutral takes the paragraph direction.
 * Each run keeps its characters in logical order; the renderer shapes an RTL run and draws it as a whole
 * (`runGlyphOrder` says whether the shaper reverses it).
 */
export function bidiRuns(text: string, base: TextDir): TextRun[] {
  const chars = [...text];
  const strong = chars.map(charDir);
  const resolved: TextDir[] = chars.map((_, i) => {
    const own = strong[i];
    if (own) return own;
    let before: TextDir | null = null;
    for (let j = i - 1; j >= 0 && !before; j--) before = strong[j];
    let after: TextDir | null = null;
    for (let j = i + 1; j < chars.length && !after; j++) after = strong[j];
    return before && before === after ? before : base;
  });
  const runs: TextRun[] = [];
  chars.forEach((ch, i) => {
    const dir = resolved[i];
    const last = runs[runs.length - 1];
    if (last && last.dir === dir) last.text += ch;
    else runs.push({ text: ch, dir });
  });
  return base === 'rtl' ? runs.reverse() : runs;
}

const MIRRORED: Record<string, string> = { '(': ')', ')': '(', '[': ']', ']': '[', '{': '}', '}': '{', '<': '>', '>': '<', '«': '»', '»': '«', '‹': '›', '›': '‹' };

/**
 * The string to hand the shaper for one run. A run with Arabic letters goes as is: the shaper detects the script,
 * joins the letters and reverses the run. An RTL run of punctuation only (a trailing "." after a Latin word in an
 * Arabic line) is not reversed by the shaper, so it is reversed and mirrored here.
 */
export function runShapingText(run: TextRun): string {
  if (run.dir === 'ltr' || ARABIC_CHAR.test(run.text)) return run.text;
  return [...run.text]
    .reverse()
    .map((ch) => MIRRORED[ch] ?? ch)
    .join('');
}

// ---- the poster's content ----

export interface PosterBlock {
  lang: Lang;
  dir: TextDir;
  headline: string;
  cashier: string;
  steps: readonly PosterStep[];
  noWhatsApp: string;
  linkHint: string;
}

export interface PosterContent {
  version: number;
  venueName: string;
  pickupPointName: string;
  /** The pickup point's customer URL: what the QR code encodes and the link printed under it. */
  url: string;
  mode: NumberMode;
  /** Default language first; one or two blocks. */
  blocks: PosterBlock[];
  /** The venue logo stored in R2, when there is one; its etag is part of the fingerprint so a new logo rebuilds the poster. */
  logo: { key: string; etag: string } | null;
}

export interface PosterInput {
  venueName: string;
  pickupPointName: string;
  url: string;
  mode: NumberMode;
  defaultLang: Lang;
  languages: readonly Lang[];
  posterLang2: Lang | null | undefined;
  logo?: { key: string; etag: string } | null;
}

function block(lang: Lang, mode: NumberMode): PosterBlock {
  const c = POSTER_COPY[lang];
  const dir: TextDir = lang === 'ar' ? 'rtl' : 'ltr';
  return {
    lang,
    dir,
    headline: posterText(c.headline),
    cashier: posterText(mode === 'scan' ? c.cashier_scan : c.cashier_ticket),
    steps: c.steps.map((s) => ({ title: posterText(s.title), body: posterText(s.body) })),
    noWhatsApp: posterText(c.no_whatsapp),
    linkHint: posterText(c.link_hint),
  };
}

/** Everything the PDF prints, resolved from the venue, the pickup point and the copy. */
export function posterContent(input: PosterInput): PosterContent {
  return {
    version: POSTER_VERSION,
    venueName: posterText(input.venueName),
    pickupPointName: posterText(input.pickupPointName),
    url: input.url,
    mode: input.mode,
    blocks: posterLanguages(input).map((l) => block(l, input.mode)),
    logo: input.logo ?? null,
  };
}

/**
 * The text a poster's storage key is hashed from: every input that changes the printed page (venue name, pickup point
 * name, URL, mode, languages, logo, layout version). A change to any of them gives a new key, so the next download
 * renders a fresh PDF; an unchanged venue gets the stored file.
 */
export function posterFingerprintInput(c: PosterContent): string {
  return JSON.stringify([c.version, c.venueName, c.pickupPointName, c.url, c.mode, c.blocks.map((b) => b.lang), c.logo ? [c.logo.key, c.logo.etag] : null]);
}

/** ASCII slug for file names: accents dropped, anything else becomes a dash; empty for an all-Arabic name. */
function slug(s: string): string {
  return s
    .normalize('NFKD')
    .replace(/[\u0300-\u036F]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .replace(/-+$/g, '');
}

/** "ringring-poster-snack-demo-comptoir-1-a4.pdf"; the pickup point id stands in for a name with no Latin letters. */
export function posterFileName(venueName: string, pickupPoint: { id: string; name: string }, size: PosterSize): string {
  const parts = [slug(venueName), slug(pickupPoint.name) || slug(pickupPoint.id)].filter((p) => p !== '');
  return `ringring-poster-${parts.join('-')}-${size}.pdf`;
}
